/**
 * MessageTemplatesPage — gerenciamento dos Modelos de Mensagens (PRO).
 *
 * Lista por categoria, cria/edita/exclui, e o editor traz os "chips" de
 * variáveis clicáveis + PREVIEW AO VIVO com dados de exemplo, para o usuário ver
 * exatamente o que o cliente vai receber antes de salvar.
 *
 * Gates: `message_templates` (PRO) para entrar; `useRole().canWrite` para editar
 * e owner/admin para excluir — espelhando a RLS, que é o portão de verdade.
 * O gerenciamento é ONLINE-ONLY (ver useMessageTemplates.ts).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Crown, Lock, MessageSquareText, Pencil, Plus, Trash2, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetwork } from '@/contexts/NetworkContext';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import {
  useCreateMessageTemplate,
  useDeleteMessageTemplate,
  useMessageTemplates,
  useSeedDefaultTemplates,
  useUpdateMessageTemplate,
  type MessageTemplate,
} from '@/hooks/useMessageTemplates';
import {
  MESSAGE_TEMPLATE_CATEGORIES,
  VARIABLES_BY_CATEGORY,
  categoryLabel,
  insertToken,
  renderTemplate,
  type MessageTemplateCategory,
} from '@/lib/messageTemplates';

/** Dados fictícios do preview — mostram a variável já formatada em pt-BR. */
const PREVIEW_CONTEXT = {
  cliente: 'Maria Silva',
  numero: '12',
  valor: 1850.5,
  validade: '2026-08-15',
  data: '2026-07-25',
  link: 'https://orcamigo.com.br/o/exemplo',
  empresa: 'Sua Empresa',
  titulo: 'Prestação de serviço',
};

const MAX_NAME = 80;
const MAX_BODY = 2000;

export default function MessageTemplatesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isOnline } = useNetwork();
  const feature = useFeature('message_templates');
  // `canDelete` é owner/admin — exatamente a policy de DELETE da tabela.
  const { canWrite, canDelete } = useRole();

  const { templates, isLoading } = useMessageTemplates();
  const seedDefaults = useSeedDefaultTemplates();
  const createTemplate = useCreateMessageTemplate();
  const updateTemplate = useUpdateMessageTemplate();
  const deleteTemplate = useDeleteMessageTemplate();

  const [category, setCategory] = useState<MessageTemplateCategory>('quote');
  const [editing, setEditing] = useState<MessageTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<MessageTemplate | null>(null);

  // Semeia os modelos padrão na primeira abertura (POR EMPRESA). O ref guarda o
  // id da empresa já tentada, não um booleano: com um booleano, trocar de
  // empresa nesta tela nunca semearia a nova (o guard continuaria ligado).
  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    const companyId = activeCompany?.id ?? null;
    if (!companyId || isLoading || !feature.allowed || !canWrite) return;
    if (seededForRef.current === companyId) return;
    seededForRef.current = companyId;
    if (templates.length > 0) return;
    void seedDefaults(templates.length);
  }, [activeCompany?.id, isLoading, templates.length, feature.allowed, canWrite, seedDefaults]);

  const visible = useMemo(
    () => templates.filter((t) => t.category === category),
    [templates, category],
  );

  if (!user) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/settings')} />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <MessageSquareText className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Faça login para gerenciar seus modelos</p>
            <Button3D className="mt-4" onClick={() => navigate('/auth')}>
              Entrar
            </Button3D>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  // Gate PRO: só CRIAR modelos é pago — o envio continua grátis com o texto padrão.
  if (!feature.allowed) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/settings')} />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Modelos de mensagens</h2>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              Monte mensagens prontas com variáveis como {'{cliente}'} e {'{valor}'} e reutilize a cada
              envio. Disponível no plano PRO.
            </p>
            <p className="mt-3 max-w-xs text-xs text-muted-foreground">
              Enviar orçamentos e contratos pelo WhatsApp continua liberado para todos, com a mensagem
              padrão do app.
            </p>
            <Button3D color="amber" className="mt-5" onClick={() => feature.require()}>
              <Crown className="mr-2 h-4 w-4" />
              Desbloquear com PRO
            </Button3D>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Header
        onBack={() => navigate('/settings')}
        action={
          canWrite ? (
            <Button3D size="sm" onClick={() => setCreating(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              Novo
            </Button3D>
          ) : undefined
        }
      />

      {!isOnline && (
        <Card className="mb-3 border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20">
          <CardContent className="flex items-center gap-3 p-3">
            <WifiOff className="h-5 w-5 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Sem conexão: dá para consultar os modelos, mas criar e editar exige internet. O envio
              pelo WhatsApp continua funcionando.
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs value={category} onValueChange={(v) => setCategory(v as MessageTemplateCategory)}>
        {/* `justify-start`: o TabsList padrão centraliza, e conteúdo mais largo
            que o container transborda pelos DOIS lados — o `overflow-x-auto`
            não revela o lado esquerdo, porque não existe rolagem negativa.
            Medido a 360px: "Orçamento" nascia em x=-35 e "Geral" terminava em
            x=395, ou seja, os dois extremos inalcançáveis. */}
        <TabsList className="w-full justify-start overflow-x-auto">
          {MESSAGE_TEMPLATE_CATEGORIES.map((c) => (
            <TabsTrigger key={c.value} value={c.value} className="shrink-0 whitespace-nowrap">
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <p className="mt-3 text-xs text-muted-foreground">
        {MESSAGE_TEMPLATE_CATEGORIES.find((c) => c.value === category)?.description}
      </p>

      <div className="mt-3 space-y-3">
        {isLoading && templates.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">Carregando…</p>
        ) : visible.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <MessageSquareText className="mb-4 h-12 w-12 text-muted-foreground" />
              <p className="font-medium">Nenhum modelo em {categoryLabel(category)}</p>
              <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                Sem modelo aqui, o envio usa a mensagem padrão do app.
              </p>
              {canWrite && (
                <Button3D className="mt-4" onClick={() => setCreating(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Criar modelo
                </Button3D>
              )}
            </CardContent>
          </Card>
        ) : (
          visible.map((template) => (
            <Card key={template.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{template.name}</p>
                      {template.is_default && (
                        <Badge variant="secondary" className="text-[10px]">
                          Padrão
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                      {template.body}
                    </p>
                  </div>
                  {canWrite && (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Editar modelo"
                        onClick={() => setEditing(template)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Excluir modelo"
                          onClick={() => setPendingDelete(template)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <TemplateFormModal
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
        template={editing}
        initialCategory={category}
        saving={createTemplate.isPending || updateTemplate.isPending}
        onSubmit={async (values) => {
          if (editing) {
            await updateTemplate.mutateAsync({ id: editing.id, updates: values });
            toast.success('Modelo atualizado.');
          } else {
            await createTemplate.mutateAsync(values);
            toast.success('Modelo criado.');
          }
          setCreating(false);
          setEditing(null);
        }}
      />

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir modelo</AlertDialogTitle>
            <AlertDialogDescription>
              O modelo “{pendingDelete?.name}” será removido para toda a equipe. Os envios passam a
              usar outro modelo da categoria ou a mensagem padrão do app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const target = pendingDelete;
                setPendingDelete(null);
                if (!target) return;
                try {
                  await deleteTemplate.mutateAsync(target.id);
                  toast.success('Modelo excluído.');
                } catch {
                  // toast tratado na mutação
                }
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}

// ===========================================================================
// Editor
// ===========================================================================

interface TemplateFormValues {
  name: string;
  category: MessageTemplateCategory;
  body: string;
  is_default: boolean;
}

function TemplateFormModal({
  open,
  onOpenChange,
  template,
  initialCategory,
  saving,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: MessageTemplate | null;
  initialCategory: MessageTemplateCategory;
  saving: boolean;
  onSubmit: (values: TemplateFormValues) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<MessageTemplateCategory>(initialCategory);
  const [body, setBody] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Recarrega o formulário toda vez que o modal ABRE (ou troca de modelo).
  // Sem isso, reabrir para criar traria o texto do último modelo editado.
  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? '');
    setCategory((template?.category as MessageTemplateCategory) ?? initialCategory);
    setBody(template?.body ?? '');
    setIsDefault(Boolean(template?.is_default));
  }, [open, template, initialCategory]);

  const variables = VARIABLES_BY_CATEGORY[category] ?? [];
  const preview = renderTemplate(body, PREVIEW_CONTEXT);

  const addToken = (token: string) => {
    const el = bodyRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? start;
    const next = insertToken(body, token, start, end);
    setBody(next.body);
    // Devolve o foco e recoloca o cursor depois do token inserido. `setTimeout`
    // e não `requestAnimationFrame`: o rAF fica PAUSADO em aba oculta/WebView sem
    // foco e o cursor nunca voltaria (ver CLAUDE.md #18).
    setTimeout(() => {
      el?.focus();
      el?.setSelectionRange(next.cursor, next.cursor);
    }, 0);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.warning('Dê um nome ao modelo.');
      return;
    }
    if (!body.trim()) {
      toast.warning('Escreva a mensagem do modelo.');
      return;
    }
    try {
      await onSubmit({ name: name.trim(), category, body, is_default: isDefault });
    } catch {
      // toast tratado na mutação
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{template ? 'Editar modelo' : 'Novo modelo'}</DialogTitle>
          <DialogDescription>
            Toque numa variável para inseri-la no texto. Ela vira o dado real na hora do envio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Nome</Label>
            <Input
              id="template-name"
              value={name}
              maxLength={MAX_NAME}
              placeholder="Ex.: Cobrança educada"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Categoria</Label>
            <div className="flex flex-wrap gap-2">
              {MESSAGE_TEMPLATE_CATEGORIES.map((c) => (
                <Button
                  key={c.value}
                  type="button"
                  variant={category === c.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategory(c.value)}
                >
                  {c.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-body">Mensagem</Label>
            <Textarea
              id="template-body"
              ref={bodyRef}
              value={body}
              maxLength={MAX_BODY}
              rows={6}
              className="resize-none"
              placeholder="Olá {cliente}! Segue o orçamento {numero} no valor de {valor}."
              onChange={(e) => setBody(e.target.value)}
            />
            <div className="flex flex-wrap gap-1.5">
              {variables.map((v) => (
                <Button
                  key={v.key}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-full px-2.5 text-xs font-mono"
                  title={v.description}
                  onClick={() => addToken(v.token)}
                >
                  {v.token}
                </Button>
              ))}
            </div>
            <p className="text-right text-[11px] text-muted-foreground">
              {body.length}/{MAX_BODY}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Prévia</Label>
            <div className="whitespace-pre-wrap break-words rounded-lg border bg-muted/40 p-3 text-sm">
              {preview || (
                <span className="text-muted-foreground">A prévia aparece conforme você escreve.</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Exemplo com dados fictícios. No envio, entram os dados reais do documento.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">Usar como padrão</p>
              <p className="text-xs text-muted-foreground">
                Vem pré-selecionado ao enviar um documento de {categoryLabel(category)}.
              </p>
            </div>
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button3D color="gray" className="flex-1" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button3D>
          <Button3D className="flex-1" disabled={saving} onClick={submit}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Header({ onBack, action }: { onBack: () => void; action?: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-3">
      <Button variant="ghost" size="icon" onClick={onBack}>
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <h1 className="flex-1 text-xl font-semibold">Modelos de mensagens</h1>
      {action}
    </div>
  );
}
